import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { TRYON_COLORS } from "../colors";
import { useI18n } from "../i18n";
import LanguageToggle from "../components/LanguageToggle";

interface TryOnPageProps {
  code: string;
}

// Identifies a picked file well enough to spot "same photo again" within a
// device: name + size + last-modified. "preset" = the link's default dress.
function fileKey(file: File | null): string {
  return file ? `${file.name}|${file.size}|${file.lastModified}` : "preset";
}

function getSessionId(code: string): string {
  const key = `tryon-session-${code}`;
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  return id;
}

function ConsentModal({ onAgree }: { onAgree: () => void }) {
  const { t } = useI18n();
  const [checked, setChecked] = useState(false);
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-6">
      <div className="w-full max-w-md rounded-t-softer bg-white p-5 pb-8 shadow-card sm:rounded-softer sm:pb-5">
        <div className="flex items-start justify-between">
          <h2 className="font-serif text-xl text-charcoal">{t.tryon.consentTitle}</h2>
          <LanguageToggle className="-mr-2 -mt-1" />
        </div>
        <p className="mt-2 text-sm leading-relaxed text-charcoal-muted">
          {t.tryon.consentBody}
        </p>
        <label className="mt-4 flex min-h-[44px] cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            className="mt-0.5 h-5 w-5 shrink-0 accent-sage-500"
          />
          <span className="text-sm text-charcoal">
            {t.tryon.consentAgreePrefix}
            <a
              href="/terms"
              target="_blank"
              rel="noreferrer"
              className="text-sage-600 underline"
            >
              {t.tryon.termsNoticeLink}
            </a>
            .
          </span>
        </label>
        <button
          onClick={onAgree}
          disabled={!checked}
          className="mt-4 w-full min-h-[48px] rounded-softer bg-sage-500 font-semibold text-white transition-colors hover:bg-sage-600 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {t.tryon.agreeContinue}
        </button>
      </div>
    </div>
  );
}

function PhotoTile({
  label,
  hint,
  emoji,
  preview,
  presetPreview,
  onSelect,
  disabled,
}: {
  label: string;
  hint: string;
  emoji: string;
  preview: string | null;
  presetPreview?: string | null;
  onSelect: (file: File) => void;
  disabled?: boolean;
}) {
  const { t } = useI18n();
  const inputRef = useRef<HTMLInputElement>(null);
  const shown = preview ?? presetPreview ?? null;
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => inputRef.current?.click()}
      className="relative aspect-[3/4] w-full overflow-hidden rounded-softer border-2 border-dashed border-linen-300 bg-white transition-colors hover:border-sage-400 focus:outline-none focus:ring-2 focus:ring-sage-400 disabled:opacity-50"
    >
      {shown ? (
        <>
          <img src={shown} alt={label} className="absolute inset-0 h-full w-full object-cover" />
          <span className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-black/55 px-3 py-1 text-xs text-white whitespace-nowrap">
            {preview ? t.tryon.tapToReplace : t.tryon.ourPick}
          </span>
        </>
      ) : (
        <span className="flex h-full flex-col items-center justify-center gap-1 px-3 text-center">
          <span className="text-3xl" aria-hidden>
            {emoji}
          </span>
          <span className="font-medium text-charcoal">{label}</span>
          <span className="text-xs text-charcoal-muted">{hint}</span>
        </span>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onSelect(file);
          e.target.value = "";
        }}
      />
    </button>
  );
}

export default function TryOnPage({ code }: TryOnPageProps) {
  const { t } = useI18n();
  const sessionId = useMemo(() => getSessionId(code), [code]);
  const link = useQuery(api.links.getByCode, { code });
  const results = useQuery(api.images.listByLink, { linkCode: code });
  const availableColorNames = useQuery(api.settings.getAvailableColors);
  const generateUploadUrl = useMutation(api.storage.generateUploadUrl);
  const createTryOn = useMutation(api.images.createTryOn);
  const touchSession = useMutation(api.sessions.touch);
  const recordConsent = useMutation(api.sessions.recordConsent);

  const [consented, setConsented] = useState(
    () => localStorage.getItem(`tryon-consent-${code}`) === "yes"
  );

  const handleConsent = () => {
    localStorage.setItem(`tryon-consent-${code}`, "yes");
    setConsented(true);
    recordConsent({ linkCode: code, sessionId });
  };

  const [personFile, setPersonFile] = useState<File | null>(null);
  const [personPreview, setPersonPreview] = useState<string | null>(null);
  const [dressFile, setDressFile] = useState<File | null>(null);
  const [dressPreview, setDressPreview] = useState<string | null>(null);
  const [color, setColor] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Combinations (photo + dress + color) already generated on this device,
  // used to warn before spending a try on a duplicate.
  const combosStorageKey = `tryon-combos-${code}`;
  const [generatedCombos, setGeneratedCombos] = useState<string[]>(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(combosStorageKey) ?? "[]");
      return Array.isArray(stored) ? stored : [];
    } catch {
      return [];
    }
  });
  const [duplicateWarning, setDuplicateWarning] = useState(false);

  useEffect(() => {
    touchSession({ linkCode: code, sessionId, userAgent: navigator.userAgent });
  }, [code, sessionId, touchSession]);

  const selectPerson = (file: File) => {
    setPersonFile(file);
    setDuplicateWarning(false);
    setPersonPreview((old) => {
      if (old) URL.revokeObjectURL(old);
      return URL.createObjectURL(file);
    });
  };

  const selectDress = (file: File) => {
    setDressFile(file);
    setDuplicateWarning(false);
    setDressPreview((old) => {
      if (old) URL.revokeObjectURL(old);
      return URL.createObjectURL(file);
    });
  };

  const selectColor = (name: string | null) => {
    setColor(name);
    setDuplicateWarning(false);
  };

  const upload = async (file: File): Promise<Id<"_storage">> => {
    const uploadUrl = await generateUploadUrl();
    const response = await fetch(uploadUrl, {
      method: "POST",
      headers: { "Content-Type": file.type },
      body: file,
    });
    const { storageId } = await response.json();
    return storageId as Id<"_storage">;
  };

  const hasDress = !!dressFile || !!link?.dressUrl;
  const comboKey = `${fileKey(personFile)}::${fileKey(dressFile)}::${color ?? "as-is"}`;

  const handleGo = async (force = false) => {
    if (!personFile || !hasDress || isSubmitting) return;

    // Warn once when this exact photo + dress + color was already generated
    if (!force && generatedCombos.includes(comboKey)) {
      setDuplicateWarning(true);
      return;
    }
    setDuplicateWarning(false);

    setIsSubmitting(true);
    setError(null);
    try {
      const [personStorageId, dressStorageId] = await Promise.all([
        upload(personFile),
        dressFile ? upload(dressFile) : Promise.resolve(undefined),
      ]);
      await createTryOn({
        linkCode: code,
        sessionId,
        personStorageId,
        dressStorageId,
        color: color ?? undefined,
      });
      setGeneratedCombos((old) => {
        const next = old.includes(comboKey) ? old : [...old, comboKey];
        localStorage.setItem(combosStorageKey, JSON.stringify(next));
        return next;
      });
    } catch (e) {
      console.error("Try-on failed:", e);
      const message = e instanceof Error ? e.message : "";
      if (message.includes("NO_TRIES_LEFT")) {
        setError(t.tryon.errorNoTries);
      } else if (message.includes("LINK_DISABLED")) {
        setError(t.tryon.errorLinkPaused);
      } else {
        setError(t.tryon.errorGeneric);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Link lookup states
  if (link === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-linen-50 text-charcoal-muted">
        {t.common.loading}
      </div>
    );
  }
  if (link === null) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-2 bg-linen-50 px-6 text-center">
        <span className="text-4xl" aria-hidden>🥀</span>
        <h1 className="font-serif text-2xl text-charcoal">{t.tryon.linkNotFoundTitle}</h1>
        <p className="text-sm text-charcoal-muted">{t.tryon.linkNotFoundHint}</p>
      </div>
    );
  }
  if (!link.isActive) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-2 bg-linen-50 px-6 text-center">
        <span className="text-4xl" aria-hidden>🌙</span>
        <h1 className="font-serif text-2xl text-charcoal">{t.tryon.linkPausedTitle}</h1>
        <p className="text-sm text-charcoal-muted">
          {t.tryon.linkPausedHint}
        </p>
      </div>
    );
  }

  const outOfTries = link.triesLeft !== null && link.triesLeft <= 0;
  const inProgress = !!results?.some((r) => r.status === "pending" || r.status === "generating");
  const canGo = !!personFile && hasDress && !isSubmitting && !outOfTries && !inProgress;
  // null/undefined = setting never saved → all colors available
  const colors =
    availableColorNames == null
      ? TRYON_COLORS
      : TRYON_COLORS.filter((c) => availableColorNames.includes(c.name));

  return (
    <div className="min-h-screen bg-linen-50">
      {!consented && <ConsentModal onAgree={handleConsent} />}
      <div className="mx-auto max-w-md px-4 py-8 pb-16">
        <header className="mb-6 text-center">
          <h1 className="font-serif text-3xl font-medium text-charcoal">{link.title}</h1>
          <p className="mt-1 text-sm text-charcoal-muted">
            {t.tryon.subtitle(!link.dressUrl)}
          </p>
          {link.maxTries !== null && (
            <p
              className={`mt-2 inline-block rounded-full px-3 py-1 text-xs font-medium ${
                outOfTries ? "bg-red-100 text-red-700" : "bg-sage-100 text-sage-700"
              }`}
            >
              {outOfTries
                ? t.tryon.noTriesLeft
                : t.tryon.triesLeft(link.triesLeft ?? 0, link.maxTries)}
            </p>
          )}
        </header>

        {/* Photo tiles */}
        <div className="grid grid-cols-2 gap-3">
          <PhotoTile
            label={t.tryon.yourPhoto}
            hint={t.tryon.yourPhotoHint}
            emoji="🙂"
            preview={personPreview}
            onSelect={selectPerson}
            disabled={outOfTries}
          />
          <PhotoTile
            label={t.tryon.theDress}
            hint={t.tryon.theDressHint}
            emoji="👗"
            preview={dressPreview}
            presetPreview={link.dressUrl}
            onSelect={selectDress}
            disabled={outOfTries}
          />
        </div>

        {/* Color presets */}
        <div className="mt-5">
          <p className="mb-2 text-sm font-medium text-charcoal">{t.tryon.dressColor}</p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => selectColor(null)}
              className={`min-h-[44px] rounded-full border px-4 text-sm transition-colors ${
                color === null
                  ? "border-sage-500 bg-sage-500 text-white"
                  : "border-linen-300 bg-white text-charcoal hover:border-sage-400"
              }`}
            >
              {t.tryon.asIs}
            </button>
            {colors.map(({ name, hex }) => (
              <button
                key={name}
                type="button"
                onClick={() => selectColor(name)}
                className={`flex min-h-[44px] items-center gap-2 rounded-full border px-3 text-sm transition-colors ${
                  color === name
                    ? "border-sage-500 bg-white ring-2 ring-sage-400"
                    : "border-linen-300 bg-white hover:border-sage-400"
                }`}
              >
                <span
                  className="h-5 w-5 rounded-full border border-black/10"
                  style={{ backgroundColor: hex }}
                />
                {t.colorNames[name] ?? name}
              </button>
            ))}
          </div>
        </div>

        {/* Go */}
        <button
          onClick={() => handleGo()}
          disabled={!canGo}
          className="mt-6 w-full min-h-[52px] rounded-softer bg-coral-500 text-lg font-semibold text-white shadow-card transition-colors hover:bg-coral-600 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {isSubmitting
            ? t.tryon.uploading
            : inProgress
              ? t.tryon.generating
              : outOfTries
                ? t.tryon.noTriesLeft
                : t.tryon.go}
        </button>
        {duplicateWarning && (
          <div className="mt-3 rounded-softer border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm text-charcoal">
              {t.tryon.duplicateWarning}
            </p>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={() => handleGo(true)}
                className="min-h-[44px] flex-1 rounded-soft bg-coral-500 px-3 text-sm font-medium text-white transition-colors hover:bg-coral-600"
              >
                {t.tryon.generateAnyway}
              </button>
              <button
                type="button"
                onClick={() => setDuplicateWarning(false)}
                className="min-h-[44px] flex-1 rounded-soft border border-linen-300 bg-white px-3 text-sm text-charcoal transition-colors hover:border-sage-400"
              >
                {t.common.cancel}
              </button>
            </div>
          </div>
        )}
        {outOfTries && (
          <p className="mt-2 text-center text-sm text-charcoal-muted">
            {t.tryon.usedAllTries}
          </p>
        )}
        {error && <p className="mt-2 text-center text-sm text-red-600">{error}</p>}
        {inProgress && (
          <p className="mt-3 text-center text-sm text-charcoal-muted animate-pulse">
            {t.tryon.creatingLook}
          </p>
        )}

        {/* Results */}
        {results && results.length > 0 && (
          <div className="mt-8">
            <h2 className="mb-3 font-serif text-xl text-charcoal">{t.tryon.yourLooks}</h2>
            <div className="space-y-4">
              {results.map((r) => (
                <div key={r._id} className="overflow-hidden rounded-softer bg-white shadow-soft">
                  {r.status === "completed" && r.url ? (
                    <img src={r.url} alt={t.tryon.tryOnResultAlt} className="w-full" />
                  ) : r.status === "failed" ? (
                    <p className="p-4 text-sm text-red-600">
                      {t.tryon.resultFailed}
                    </p>
                  ) : (
                    <div className="flex aspect-[3/4] items-center justify-center bg-linen-100 animate-pulse text-charcoal-muted text-sm">
                      {t.tryon.generating}
                    </div>
                  )}
                  {r.tryOnColor && (
                    <p className="px-4 py-2 text-xs text-charcoal-muted">
                      {t.tryon.resultColor(t.colorNames[r.tryOnColor] ?? r.tryOnColor)}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <footer className="mt-10 text-center text-xs text-charcoal-muted">
          {t.tryon.footerNote}{" "}
          <a href="/terms" target="_blank" rel="noreferrer" className="underline">
            {t.tryon.termsShort}
          </a>
        </footer>
      </div>
    </div>
  );
}
